import get from 'lodash/get';

import { TRADEWIND } from '../../constant/colors';
import { objectKeysToCamelCase } from '../helpers/object-keys-camel-case';

import Assets from '@app/assets';
// Some app settings, like max file sizes or app revision
const appSettings = objectKeysToCamelCase(get(window, '__app_settings__', {}));

export const AppConfig = {
  appSettings,

  transferExpiryOptions: get(window, 'transferExpiryOptions', []),

  // For a film festival we need to permit larger uploads for a specific channel.
  // ^ August 31th, 2017. I can imagine we can remove that code already...
  channelSettings: objectKeysToCamelCase(get(window, 'ChannelSettings', {})),

  // Backgrounds uploaded by the user
  channelWallpapers: objectKeysToCamelCase(get(window, 'Wallpapers', [])),

  // A set of WeTransfer curated wallpapers available for users when onboarding
  curatedWallpapers: objectKeysToCamelCase(
    get(window, '__curated_wallpapers__', [])
  ),

  // Describe the runtime environment
  env: {
    development: __DEV__ === true,
    wallpaperDevelopment: __WALLPAPER_DEV_ENABLED__ === true,
  },

  // This is a global variable that should be defined by
  // loading 'advertising.js' scripts - however, if it is not
  // set this means it was blocked by ad blockers. OH MY GLOB.
  isAdvertisingEnabled: !!window['__ads_enabled__'],

  // Configuration and feature flags from LaunchDarkly
  launchDarkly: {
    clientID: get(window, '__launch_darkly__.client_id', ''),
    enabled: get(window, '__launch_darkly__.enabled', false),
    featureFlags: get(window, '__launch_darkly__.feature_flags', {}),
    user: get(window, '__launch_darkly__.user', {}),
  },

  // Google Reacaptcha configuration
  recaptcha: objectKeysToCamelCase(get(window, '__recaptcha__', {})),

  // Configuration for Stripe
  stripe: {
    ...objectKeysToCamelCase(get(window, '__stripe__', {})),
    url: 'https://js.stripe.com/v3/',
  },

  // Configuration for TrackJS
  trackJS: objectKeysToCamelCase(get(window, '__trackjs__', {})),

  // Cookie names used by the application
  cookies: {
    takeover: {
      name: 'wt_takeover',
    },
    plus: {
      name: 'wt_plus',
    },
    signout: {
      name: 'wt_signout',
    },
    featureBanner: {
      name: 'wt_banner',
    },
    transferType: {
      name: 'wt_ttype',
    },
    termsAndConditions: {
      name: 'wt_tandc',
    },
    privacy: {
      name: 'wt_privacy',
    },
    referral: {
      name: 'wt_trk',
    },
    firstVisit: {
      name: 'wt_first_visit',
    },
    snowplow: {
      name: '_wt_snowplow',
    },
  },

  //The notification banner that we show next to the WeTransfer logo
  featureBanner: {
    featureTranslationKey: null, //eg: 'page.profile.wallpapers.notice'
    featureName: null,
    shouldShowFeatureBanner: false,
  },

  // Should this be part of user rules object?
  profile: {
    minPasswordLength: 6,
  },

  subdomainName: {
    maxLength: 63,
  },

  // Top right navigation config
  navigation: {
    tooltip: {
      showDelay: 2000,
      hideDelay: {
        onboarding_success: 8000,
        already_signed_in: 4000,
      },
    },
  },

  textfield: {
    debounceDelay: 300,
  },

  // Redux actions config
  actions: {
    extended_pending: 10000,
  },

  // Tooltips configuration
  notification: {
    animation_duration: 200,
    duration: 5000,
  },

  // Should this be part of transfer configuration
  transfer: {
    showWarningThreshold: 6 * 24 * 60 * 60 * 1000,
    precisionThreshold: 1024 * 1024 * 1024, // We want to show GB's with 1 decimals, and MB's without decimals
    verificationCodeLength: 6,
  },

  twoFactorAuthentication: {
    codeLength: 6,
    codeMaxLength: 13,
  },

  // Override styles for navigation routes
  route: {
    '/': {
      navStyle: {
        backgroundColor: TRADEWIND,
      },
    },
    '/pro': {
      navStyle: {
        backgroundImage: `url(${Assets.lookup('plus/header.png')})`,
      },
    },
  },

  // Panels config
  panel: {
    full_panel_breakpoint: 1049, // At this point, a half panel will become a full panel
    openDelay: 3000,
  },

  // Plus avatars for users without a profile image
  avatars: [
    Assets.lookup('avatars/01.png'),
    Assets.lookup('avatars/02.png'),
    Assets.lookup('avatars/03.png'),
    Assets.lookup('avatars/04.png'),
    Assets.lookup('avatars/05.png'),
    Assets.lookup('avatars/06.png'),
    Assets.lookup('avatars/07.png'),
    Assets.lookup('avatars/08.png'),
    Assets.lookup('avatars/09.png'),
    Assets.lookup('avatars/10.png'),
    Assets.lookup('avatars/11.png'),
    Assets.lookup('avatars/12.png'),
  ],

  // Mobile apps upsell banners
  mobileAppBanner: {
    active: true,
    downloadUrl: 'https://wetransfer.com/products',
    downloadUrliOS:
      'https://itunes.apple.com/app/apple-store/id765359021?pt=658364&ct=wetransfer-web-popup&mt=8',
    downloadUrlAndroid:
      'https://play.google.com/store/apps/details?id=com.wetransfer.app.live&referrer=utm_source%3Dwetransfer%26utm_medium%3Dweb-popup',
    dynamicLinkBaseUrl: location.host.includes('wetransfer.com')
      ? 'https://collectapp.page.link/'
      : 'https://debugcollectapp.page.link/',
    dynamicLinkParams: {
      st: 'Collect by WeTransfer',
      sd: 'The best way to save, organize and share ideas from all your apps',
      efr: '1',
      ibi: 'com.wetransfer.app',
      isi: 765359021,
      apn: location.host.includes('wetransfer.com')
        ? 'com.wetransfer.app.live'
        : 'com.wetransfer.app.live.debug',
      afl:
        'https://play.google.com/store/apps/details?id=com.wetransfer.app.live',
      pt: '658364',
      ct: 'wetransfer-web-popup',
      mt: '8',
      utm_source: 'wetransfer',
      utm_medium: 'web-popup',
    },
  },

  supportedBrowsers: [
    'Chrome >= 42',
    'CriOS >= 42',
    'Firefox >= 38',
    'FxiOS >= 9',
    'Safari >= 6',
    'Explorer >= 11',
    'Edge >= 12',
  ],

  // Limits for assets upload
  assetUpload: {
    wallpaperResolution: '1920 x 1200',
    wallpaperVideoResolution: '1920 x 1080',
    emailBackgroundResolution: '1600 x 800',
    ...appSettings.fileSizes,
  },
  // analytics sdks we use
  analytics: {
    sdks: {
      facebook: true,
      bing: true,
    },
  },

  termsAndConditions: {
    consentId: '1',
    consentVersion: '20190527',
  },

  privacy: {
    consentId: '3',
    consentVersion: '20190527',
  },

  payment: {
    chargeableStatus: ['chargeable', 'consumed'],
  },

  // Real time chat for Pro users
  zendesk: {
    baseUrl: 'https://wetransfer.zendesk.com/hc/en-us/',
    article_transfer_duration:
      'articles/202603916-How-long-are-uploads-available-to-download-',
  },

  // Salesforce form
  salesforce: {
    postLeadURL: __DEV__
      ? 'https://test.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8'
      : 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8',
    OID: __DEV__ ? '00D6E0000009CcS' : '00D0Y000000YKnV',
    budgetFieldID: '00N0Y00000A2Yi6', // ID for custom field generated in Salesforce
  },

  layout: {
    lists: {
      initialItemsNumber: {
        downloader: 5,
        detail: 10,
        progress: 10,
      },
    },
  },
};
